/**
 * @file src/local/localRailController.js
 * @description The local library as the collection app sees it: cards on the rail, and what to do
 * when one is dropped, staged, located or removed.
 *
 * The collection app owns the rail and the stages; this controller owns the local part of both and
 * talks to the app through a few callbacks. It merges local entries into the drawer list (always at
 * the end, in the order the person left them, remembered per collection on this machine), puts them
 * back on the stages they were left on when the library has booted, gates a local card's drop (a
 * room refuses it, a card not yet analysed waits for its coarse pass), and hands the studio the
 * props the rail's header, banner, menus and dialogs read. Whatever is dropped is analysed in the
 * background, however much of it there is: nothing is asked and nothing pauses. The peaks and
 * loudness passes run in a worker; only opening and decoding each file touches this thread, which
 * is why a long file can still be heard as a moment's interruption at the changeover.
 *
 * ## Why the placements are remembered HERE and nowhere else
 *
 * A `local:` id names a file on ONE machine. Both of the other records an arrangement lives in are
 * server-side — the collection's own saved layout, and each person's own arrangement of it — and a
 * card that exists nowhere but this browser must never travel into either. So the layout blanks a
 * stage a local file holds, and this controller keeps those placements, with the rail order they
 * belong to, in one record per collection in the local store.
 *
 * This is ONE of THREE records describing a single arrangement, not a system of its own. The other
 * two, the rules that join them, and which of them each mode uses are spelled out under "An
 * arrangement has THREE RECORDS" in `createCollectionApp.js`. Read that before changing either
 * side: a stage is a single position, whichever record names it.
 *
 * The two rules that reach into this file: every record is written from the app's one
 * `stageSourceIds()`, and this one only ever fills stages the resolved grid left EMPTY (it can
 * answer "which of my files", never "which stage"). Restoring never writes.
 */
import notifications from '../core/AppNotifications.js';
import { getT } from '../i18n/index.js';
import { voiceRegistry } from '../voice/VoiceRegistry.js';
import { isLocalEntryId } from './localAudioIds.js';
import { collectDroppedFiles, pickLocalFiles } from './localFileAccess.js';
import {
  addScopeToEntry,
  bootLocalLibrary,
  ensureLocalEntryAnalysed,
  reanalyseLocalEntry,
  importLocalFiles,
  isLocalEntryStageable,
  listLocalLibrary,
  LOCAL_STATUS,
  localAnalysisPercent,
  localCopyFailureOf,
  localCopyPercent,
  localCoverUrl,
  localFilesAreCopied,
  localScopeName,
  localStatusOf,
  locateLocalEntry,
  readLocalArrangement,
  readLocalEntryById,
  readLocalLibrarySummary,
  removeLocalEntryFromScope,
  requestLocalAccess,
  resumePendingAnalysis,
  scopesOf,
  searchFolderForMissing,
  setLocalLoudnessListener,
  setLocalTempoListener,
  subscribeToLocalLibrary,
  whenLocalCopiesSettled,
  writeLocalArrangement,
  writeLocalPanel,
} from './localLibrary.js';
import { isLocalStoreOpen, readLocalFlag, writeLocalFlag } from './localLibraryStore.js';

/** Set the first time this browser keeps a copy, so the explanation is given once and not again. */
const COPY_EXPLAINED_FLAG = 'copy-explained';

/**
 * The shortest gap between two redraws of the rail. The library emits per copied chunk and per
 * slice of every analysis pass — thousands of times during a folder import — and no one can see a
 * progress number change faster than this anyway.
 */
const PUBLISH_INTERVAL_MS = 80;

/**
 * Say once, in this browser, what copying means — then never again. The rail's own "N kept in this
 * browser" carries it from then on, which is the difference between informing someone and nagging
 * them. Shown as the copies START, so it explains what is happening rather than reporting it.
 */
async function explainCopiesOnce(t, notifications) {
  if (await readLocalFlag(COPY_EXPLAINED_FLAG)) return;
  await writeLocalFlag(COPY_EXPLAINED_FLAG, true);
  notifications.showToast(t('studio.local.copyExplained'), 'info', 9000);
}

/**
 * A file this browser could not keep is worth one line, because the alternative is discovering it
 * as a missing card after the next reload. One toast for the whole import, named by the first file.
 */
function reportCopyFailures(t, notifications, added) {
  const failures = added.map((entry) => [entry, localCopyFailureOf(entry.id)]).filter(([, code]) => code);
  if (!failures.length) return;
  const [entry, code] = failures[0];
  const key = code === 'quota' ? 'studio.local.noRoom' : 'studio.local.sessionOnlyExplained';
  notifications.showToast(t(key, { title: entry.name }), 'warning', 9000);
}

/**
 * The drawer card a local entry becomes.
 *
 * `otherCollections` is how many OTHER collections still hold this file, so the card's remove item
 * can name what it will actually do — leave this collection, or take the file off the machine.
 */
export function toLocalDrawerEntry(entry, scopeKey = null) {
  const status = localStatusOf(entry.id);
  return {
    voiceId: entry.id,
    trackId: entry.id,
    entityType: 'track',
    // What the file calls itself, and the file name when it calls itself nothing. A card is about
    // the music, and this one sits on the same rail as ours — the FILE name is still what the
    // local files page lists and what the person's own folder shows.
    title: entry.tags?.title || entry.name,
    // The artist for the same reason, falling back to the folder, which is what a card with
    // nothing else to say has always shown.
    subtitle: entry.tags?.artist || entry.folder || null,
    // The cover the file carried, if this browser kept one. Null on the first render and until the
    // read lands — the library emits then, and the card that asked draws it in place of its letter.
    image: localCoverUrl(entry.id),
    isOfficial: false,
    bootable: true,
    removable: false,
    local: {
      status,
      percent: localAnalysisPercent(entry.id),
      copyPercent: localCopyPercent(entry.id),
      folder: entry.folder ?? null,
      path: entry.path ?? null,
      durationSec: entry.durationSec ?? null,
      tempoBpm: entry.tempoBpm ?? null,
      otherCollections: scopesOf(entry).filter((key) => key !== scopeKey).length,
    },
  };
}

function skippedMessage(t, skipped) {
  const aiff = skipped.filter((s) => s.reason === 'unsupported-audio' && /aif/i.test(s.name)).length;
  const otherUnsupported = skipped.filter((s) => s.reason === 'unsupported-audio').length - aiff;
  const notAudio = skipped.filter((s) => s.reason === 'not-audio').length;
  const reasons = [];
  if (aiff) reasons.push(t('studio.local.reasonAiff'));
  if (otherUnsupported) reasons.push(t('studio.local.reasonUnsupported', { count: otherUnsupported }));
  if (notAudio) reasons.push(t('studio.local.reasonNotAudio', { count: notAudio }));
  return t('studio.local.skipped', { count: skipped.length, reasons: reasons.join(', ') });
}

/**
 * @param {object} opts
 * @param {string|null} opts.scopeKey the collection (or queue) id the arrangement is remembered under
 * @param {string|null} [opts.scopeName] the collection's name, remembered with the files so the
 *   local files page can still name the collection after it has been deleted
 * @param {(localEntries: object[]) => void} opts.applyEntries replace the rail's local cards
 * @param {(index: number, entry: object,
 *   options?: {focus?: boolean, persist?: boolean, swap?: object|null}) => void} opts.loadStage the
 *   app's own stage load. The restore passes `{focus: false, persist: false}`: nobody asked for it,
 *   so it neither takes the focus nor writes the arrangement back. `swap` is the orbiter/world that
 *   stage was left playing, when it is not the file's own.
 * @param {(props: object) => void} opts.updateStudio push new studio props
 * @param {() => boolean} opts.inRoom
 * @param {() => {railIds: string[], stageIds: Array<string|null>, stageSwaps: Map<number, object>}}
 *   [opts.readArrangement] the rail and the stages as the app holds them right now — `stageIds` by
 *   stage index, a source voiceId or null, and `stageSwaps` the orbiter/world each stage plays
 *   instead of its card's own pairing. Read at each persist moment, never captured ahead of one.
 * @param {(index: number) => boolean} [opts.stageIsFree] whether stage `index` exists and holds
 *   nothing, asked before a remembered local card is put back on it.
 */
export function createLocalRailController({
  scopeKey,
  scopeName = null,
  applyEntries,
  loadStage,
  updateStudio,
  inRoom,
  readArrangement = () => ({ railIds: [], stageIds: [], stageSwaps: new Map() }),
  stageIsFree = () => false,
}) {
  let disposed = false;
  let unsubscribe = null;
  let order = [];
  /** stage index → the local id left there, for this collection on this machine. */
  let stages = new Map();
  /** stage index → the orbiter/world that stage was left playing instead of the file's own pairing.
   *  A stage is a TRIAD, so the id alone does not say what it plays. */
  let swaps = new Map();
  /** Indexes whose remembered card this session could not stage — a file that has moved, or one the
   *  browser may not read yet. The stage is empty and the app will report it as empty, so without
   *  this the next persist would forget where that file lived. Cleared by `stageChanged` the moment
   *  the app has any opinion about that stage — a card landing on it, or it being emptied. */
  const held = new Set();
  /** False until the stored arrangement has been read. Writing before that would persist an empty
   *  rail — the local cards are not on it yet — over the one we are about to load. */
  let loaded = false;
  /** id → the stages it was dropped on before its coarse pass; loaded when the pass lands. */
  const pendingLoads = new Map();
  /** Of those, the ids the RESTORE is waiting on rather than a person — they take their stage
   *  without taking the focus with it. */
  const restoring = new Set();
  const addPending = (id, index) => {
    if (!pendingLoads.has(id)) pendingLoads.set(id, new Set());
    pendingLoads.get(id).add(index);
  };
  let missingOpen = false;
  /**
   * The local-files PANEL: whether it was left showing, and the width it was dragged to.
   *
   * Kept here rather than in the collection's layout because the panel shows THIS machine's files. In
   * the collection's layout it was one person's viewing preference stored in everyone's record: a
   * viewer opening someone else's collection inherited the editor's open panel and an empty list of
   * files they do not have. The panel belongs with the data it displays.
   *
   * `null` until the stored record is read — the studio mounts before that lands, so it must be able
   * to tell "not known yet" from "closed" and adopt the answer when it arrives.
   */
  let panel = null;
  let lastPublishedAt = 0;
  let publishTimer = null;

  function t(key, options) {
    return getT()(key, options);
  }

  function orderedEntries() {
    const entries = listLocalLibrary(scopeKey);
    const byId = new Map(entries.map((entry) => [entry.id, entry]));
    const ordered = order.map((id) => byId.get(id)).filter(Boolean);
    // A Set, not `order.includes`: this runs for every entry on every redraw, and a rail of several
    // hundred files turns a linear scan per entry into a square one.
    const known = new Set(order);
    const rest = entries.filter((entry) => !known.has(entry.id));
    order = [...ordered, ...rest].map((entry) => entry.id);
    return [...ordered, ...rest];
  }

  function studioProps() {
    const summary = readLocalLibrarySummary(scopeKey);
    return {
      // How this machine left the panel. `panelKnown` is false until the stored record has been
      // read — the studio mounts before that, so it opens closed and adopts these when they land,
      // rather than treating "not read yet" as a deliberate "closed".
      panelKnown: panel !== null,
      panelOpen: panel?.open === true,
      panelWidth: Number.isFinite(panel?.width) ? panel.width : null,
      onPanelChange: notePanel,
      // Local files are THIS machine's, and a room deals a pool every participant can see. So a
      // session that has become a room offers neither adding them nor managing them — the controls
      // are not shown at all rather than shown and then refusing.
      enabled: !inRoom(),
      platform: summary.platform,
      total: summary.total,
      missing: summary.missing,
      needPermission: summary.needPermission,
      analysing: summary.analysing,
      copies: summary.copies,
      copiedBytes: summary.copiedBytes,
      sessionOnly: summary.sessionOnly,
      missingEntries: orderedEntries()
        .filter((entry) => localStatusOf(entry.id) === LOCAL_STATUS.MISSING)
        .map((entry) => ({ id: entry.id, name: entry.name, folder: entry.folder ?? entry.path ?? null })),
      missingOpen,
      onDropFiles: dropFiles,
      onPickFiles: pickFiles,
      onAllowAccess: allowAccess,
      onAction: cardAction,
      onSearchFolder: searchFolder,
      onSetMissingOpen: setMissingOpen,
      collectionName: localScopeName(scopeKey),
      scopeKey,
      onAddToCollection: addToCollection,
      onStageEntries: stageEntries,
    };
  }

  /**
   * Redraw the rail and the studio's local props, now.
   *
   * Everything a PERSON did comes through here — a file dropped, a card removed, a dialog opened —
   * because a surface that answers a click on a timer feels broken.
   */
  function publish() {
    if (publishTimer !== null) {
      clearTimeout(publishTimer);
      publishTimer = null;
    }
    if (disposed) return;
    lastPublishedAt = Date.now();
    applyEntries(orderedEntries().map((entry) => toLocalDrawerEntry(entry, scopeKey)));
    updateStudio({ localRail: studioProps() });
  }

  /**
   * Redraw for something the LIBRARY did, at most this often.
   *
   * Importing a folder of several hundred files emits tens of thousands of times — a tick per
   * copied chunk, a tick per slice of every peaks and loudness pass — and each one rebuilds every
   * card on the rail and every row of the run's file list. Un-throttled, that arithmetic IS the
   * import: it runs on the thread the audio's control rate lives on, and it is what turned a folder
   * into a wait. Leading edge first, so a lone change still lands at once; a burst draws once now
   * and once when it stops, never in between.
   */
  function schedulePublish() {
    if (disposed) return;
    const waited = Date.now() - lastPublishedAt;
    if (waited >= PUBLISH_INTERVAL_MS) {
      publish();
      return;
    }
    if (publishTimer === null) publishTimer = setTimeout(publish, PUBLISH_INTERVAL_MS - waited);
  }

  function settlePendingLoads() {
    pendingLoads.forEach((indexes, id) => {
      const status = localStatusOf(id);
      // A card the browser cannot reach, or one that failed, does not keep waiting to be loaded:
      // a retry here would re-open the permission prompt on every library change.
      if (!status || status === LOCAL_STATUS.MISSING || status === LOCAL_STATUS.PERMISSION || status === LOCAL_STATUS.FAILED) {
        pendingLoads.delete(id);
        restoring.delete(id);
        return;
      }
      if (!isLocalEntryStageable(id) || status === LOCAL_STATUS.DEFERRED) return;
      const entry = readLocalEntryById(id);
      pendingLoads.delete(id);
      const restored = restoring.delete(id);
      if (entry) {
        indexes.forEach((index) =>
          loadStage(index, toLocalDrawerEntry(entry, scopeKey), {
            focus: !restored,
            persist: !restored,
            swap: restored ? stagedSwap(index) : null,
          }),
        );
      }
    });
  }

  /** The app loaded or cleared stage `index` by some other hand: a local card still waiting for
   *  that stage must not land on top of it later. */
  function stageChanged(index, voiceId = null) {
    // Whatever just happened to this stage — a card landed on it, or it was emptied — the app now
    // has an opinion about it, so the placement is no longer being held on the file's behalf.
    // Without this an eject could never stick: the restore does not persist, so nothing else would
    // ever release the hold, and the next persist would write the remembered file straight back.
    held.delete(index);
    pendingLoads.forEach((indexes, id) => {
      if (id === voiceId) return;
      indexes.delete(index);
      if (indexes.size === 0) {
        pendingLoads.delete(id);
        restoring.delete(id);
      }
    });
  }

  /** Loudness landed for an entry that may already be playing: hand the gain to those voices. */
  function applyLoudnessToVoices(id, loudness) {
    voiceRegistry.all().forEach((voice) => {
      const engine = voice?.audioEngine;
      const td = engine?.playbackTrackData;
      if (!td || td.trackId !== id) return;
      try {
        td.metadata = { ...(td.metadata || {}), audioNormalization: { ...loudness } };
        engine._applyNormalizationGain?.();
      } catch (error) {
        console.warn('[local] could not apply the gain to a playing voice', error);
      }
    });
  }

  /**
   * Hand a just-detected tempo to any voice already playing this entry.
   *
   * The same race the gain has, answered the same way: peaks make a card stageable before the tempo
   * pass finishes, so a file staged in that window is playing on a deck that resolved no native
   * tempo and therefore refuses to warp. `setNativeTempo` is the deck's own arrival path — it lifts
   * the no-tempo warp lock and seeds sync exactly as a typed-in BPM would — so the deck reaches the
   * state it would have booted into had the tempo been there first.
   */
  function applyTempoToVoices(id, tempoBpm) {
    if (!(tempoBpm > 0)) return;
    voiceRegistry.all().forEach((voice) => {
      const engine = voice?.audioEngine;
      const td = engine?.playbackTrackData;
      if (!td || td.trackId !== id) return;
      try {
        const metadata = { ...(td.metadata || {}) };
        const audioAnalysis = { ...(metadata.audioAnalysis || {}) };
        audioAnalysis.analysis = { ...(audioAnalysis.analysis || {}), tempo_bpm: tempoBpm };
        metadata.audioAnalysis = audioAnalysis;
        td.metadata = metadata;
        voice?.deck?.setNativeTempo?.(tempoBpm);
      } catch (error) {
        console.warn('[local] could not apply the tempo to a playing voice', error);
      }
    });
  }

  async function start() {
    const stored = scopeKey ? await readLocalArrangement(scopeKey) : { ids: [], stages: {} };
    if (disposed) return;
    // A panel the person moved while this was still reading WINS over what was stored: it is the
    // newer statement, and it is the one they are looking at.
    panel = { ...(stored.panel && typeof stored.panel === 'object' ? stored.panel : {}), ...(panel ?? {}) };
    order = stored.ids;
    stages = new Map(
      Object.entries(stored.stages ?? {})
        .map(([index, id]) => [Number(index), id])
        .filter(([index, id]) => Number.isInteger(index) && index >= 0 && isLocalEntryId(id)),
    );
    swaps = new Map(
      Object.entries(stored.swaps ?? {})
        .map(([index, swap]) => [Number(index), swap])
        .filter(([index, swap]) => Number.isInteger(index) && stages.has(index) && swap && typeof swap === 'object'),
    );
    // HELD from the moment the record is read, not from the restore. The studio is already
    // interactive while the library is still opening (presence, the vault sweep), so a persist can
    // land in that window with no local card on the rail yet — and would delete every placement it
    // could not see. Released per stage as something actually takes it (`stageChanged`).
    stages.forEach((_, index) => held.add(index));
    unsubscribe = subscribeToLocalLibrary(() => {
      // Never throttled: a card that has just become playable has a stage waiting for it.
      settlePendingLoads();
      schedulePublish();
    });
    setLocalLoudnessListener(applyLoudnessToVoices);
    setLocalTempoListener(applyTempoToVoices);
    await bootLocalLibrary();
    if (disposed) {
      unsubscribe?.();
      return;
    }
    resumePendingAnalysis(scopeKey);
    publish();
    // Only NOW may this record be written, and the ordering is the whole point: `publish()` is what
    // puts the local cards on the rail. Set any earlier — as it was, right after the meta read — a
    // persist landing while the library was still opening would read a rail with no local cards on
    // it yet and write `ids: []` straight over the order it had just loaded, in memory and on disk.
    // `held` protects the STAGES through that window; nothing protected the rail order.
    //
    // A boot that never got this far leaves it false, which is the safe direction: a library that
    // could not open must not write a record describing what it could not see.
    loaded = true;
    restoreStages();
  }

  /**
   * Put the local files back on the stages they were left on.
   *
   * Run once, after the library has booted and the cards are on the rail, and only for what is
   * actually reachable — a file whose original has moved, or which this browser may not read yet,
   * stays in the rail showing exactly what it shows today. No prompt and no dialog: nobody asked
   * for anything here, and a reload that opens a permission sheet or a locator is a reload that
   * interrupts. A card mid-analysis waits on the same `pendingLoads` a drop uses, so the restore
   * queues nothing of its own and does not fight the analysis queue.
   */
  function restoreStages() {
    if (!stages.size) return;
    const present = new Map(listLocalLibrary(scopeKey).map((entry) => [entry.id, entry]));
    const load = [];
    for (const [index, id] of [...stages]) {
      const entry = present.get(id);
      // Removed from this collection, or off the machine altogether: the placement names nothing.
      //
      // ...but only when the library can actually SPEAK for what is on this machine. A blocked or
      // repaired IndexedDB open makes `bootLocalLibrary` report an EMPTY library rather than fail
      // (see `isLocalStoreOpen`), and every placement would then read as "the file is gone". The
      // deletions below are in memory, which is not the protection it looks like: they are exactly
      // the state the next persist serializes, and dropping the HOLD with them removes the one
      // thing that would have kept the placement. One bad open would forget every stage for good.
      if (!entry) {
        if (!isLocalStoreOpen()) continue;
        stages.delete(index);
        swaps.delete(index);
        held.delete(index);
        continue;
      }
      // A hosted track already holds this stage (the collection's own saved layout restored it, and
      // that layout is everyone's). It is not evicted by one machine's file, and the placement is
      // released: the stage is genuinely taken.
      if (!stageIsFree(index)) {
        held.delete(index);
        continue;
      }
      if (isLocalEntryStageable(id) && entry.peaks) {
        load.push([index, entry]);
        continue;
      }
      const status = localStatusOf(id);
      // Still being analysed or copied: it lands on its stage when the pass does.
      if (status === LOCAL_STATUS.ANALYSING || status === LOCAL_STATUS.COPYING) {
        addPending(id, index);
        restoring.add(id);
      }
      // Anything else — moved, not readable yet, failed — stays in the rail exactly as it is, and
      // its stage stays remembered. Locating the file does NOT re-stage it in this session (the
      // restore runs once, at boot); it comes back on the next reload.
    }
    // Deliberately NOT written here. `present` is this tab's in-memory library, and a boot whose
    // IndexedDB open was blocked or dropped reports an EMPTY library rather than failing — every
    // placement would read as "the file is gone" and the whole record would be erased on the way
    // back up. The prune above is enough: it is in memory, and the next real persist carries it.
    // Loaded only once every stage has been decided, so the persist each one sets off sees the
    // finished picture rather than a half-restored one.
    // Neither focused nor persisted: nobody asked for this, and writing the arrangement back from
    // inside its own restore is what destroys it (see `replaceStage` in `createCollectionApp.js`).
    load.forEach(([index, entry]) =>
      loadStage(index, toLocalDrawerEntry(entry, scopeKey), {
        focus: false,
        persist: false,
        swap: stagedSwap(index),
      }),
    );
  }

  function dispose() {
    disposed = true;
    if (publishTimer !== null) clearTimeout(publishTimer);
    publishTimer = null;
    unsubscribe?.();
    setLocalLoudnessListener(null);
    setLocalTempoListener(null);
  }

  /**
   * The pairing stage `index` was left playing, when it is not the file's own.
   *
   * Handed to `loadStage` rather than overlaid onto the card here: the app both boots the stage
   * with it AND records it, and a pairing carried only on the entry would look unswapped to the
   * next swap on that stage. The rail also goes on showing the file as the library holds it, not
   * one stage's version of it.
   */
  function stagedSwap(index) {
    const swap = swaps.get(index);
    return swap && (swap.orbiterId || swap.entangledWorldId) ? swap : null;
  }

  /**
   * The panel was opened, put away, or dragged to a new width.
   *
   * Written straight through rather than waiting for the app's persist moment: those are triggered
   * by things that change the ARRANGEMENT, and putting a panel away is not one of them — an inline
   * session has no arrangement write at all and would otherwise never remember it.
   */
  function notePanel(next) {
    if (!scopeKey) return;
    const merged = { ...(panel ?? {}) };
    if (typeof next?.open === 'boolean') merged.open = next.open;
    if (Number.isFinite(next?.width)) merged.width = next.width;
    panel = merged;
    // Its OWN write, touching only the panel's part of the row. So it is safe at any moment —
    // including before `start()` has read the record, when the rail in memory is still empty — and
    // safe against another tab: writing the panel never carries a copy of the rail with it, so it
    // cannot put back placements somebody changed elsewhere.
    writeLocalPanel(scopeKey, panel).catch(() => {});
  }

  /** Returns the write, so a caller that needs it settled can wait; the app never does. */
  function writeArrangement() {
    if (!scopeKey) return Promise.resolve();
    return writeLocalArrangement(scopeKey, {
      ids: order,
      stages: Object.fromEntries(stages),
      swaps: Object.fromEntries(swaps),
    });
  }

  /**
   * The app's persist moment: remember the local rail and its stages here, since the collection
   * layout cannot hold either. Read through `readArrangement` rather than from an argument, so what
   * is written is the arrangement as it stands at this moment and not one captured earlier.
   *
   * Ungated. Every gate on the collection's own persist — a room, no edit rights, no collection id —
   * is about the collection's document, which a local id never enters. Someone with no rights over
   * a collection can still put their own files on its stages, and this is where that is kept.
   */
  function persistArrangement() {
    if (!loaded || !scopeKey) return Promise.resolve();
    const { railIds, stageIds, stageSwaps } = readArrangement();
    order = (Array.isArray(railIds) ? railIds : []).filter(isLocalEntryId);
    (Array.isArray(stageIds) ? stageIds : []).forEach((id, index) => {
      if (isLocalEntryId(id)) {
        // A DIFFERENT file on this stage plays its own pairing: the swap belonged to the file that
        // was there, and keeping it would put the previous file's orbiter on this one at the next
        // restore. The app clears its own record on a replace; this is the same fact on this side.
        if (stages.get(index) !== id) swaps.delete(index);
        stages.set(index, id);
        held.delete(index);
      } else if (id) {
        // Something else took the stage.
        stages.delete(index);
        swaps.delete(index);
        held.delete(index);
      } else if (!held.has(index)) {
        stages.delete(index);
        swaps.delete(index);
      }
    });
    // The pairing each of THIS record's stages is playing, taken from the app's own live record of
    // it — one owner of that fact for hosted cards and local files alike.
    //
    // Driven by the STAGES this record names, not by the entries the app happens to have: a swap
    // the app has cleared is absent from `live`, and a loop over `live` alone would never reach it,
    // so a pairing the person reset would be written back and restored on the next boot.
    //
    // A HELD stage keeps whatever it was remembered with. The app reports it as empty because the
    // file could not be put back, not because its pairing changed.
    const live = stageSwaps instanceof Map ? stageSwaps : new Map(Object.entries(stageSwaps ?? {}));
    for (const index of [...swaps.keys()]) {
      if (!stages.has(index)) swaps.delete(index);
    }
    for (const index of stages.keys()) {
      if (held.has(index)) continue;
      const swap = live.get(index) ?? live.get(String(index));
      const shaped = {};
      if (swap?.orbiterId) shaped.orbiterId = swap.orbiterId;
      if (swap?.entangledWorldId) shaped.entangledWorldId = swap.entangledWorldId;
      if (shaped.orbiterId || shaped.entangledWorldId) swaps.set(index, shaped);
      else swaps.delete(index);
    }
    return writeArrangement();
  }

  /** The gate a local card passes before the app loads it. Returns true when the app may proceed. */
  function beforeLoadStage(index, entry) {
    const id = entry?.voiceId;
    if (!isLocalEntryId(id)) return true;
    if (inRoom()) {
      notifications.showToast(t('studio.local.roomDropRefused'), 'warning', 5000);
      return false;
    }
    const status = localStatusOf(id);
    if (status === LOCAL_STATUS.MISSING || status === LOCAL_STATUS.PERMISSION) {
      // Staging a dimmed card asks for permission first, then loads (the design's 3b).
      if (status === LOCAL_STATUS.PERMISSION) {
        addPending(id, index);
        void allowAccess();
      } else {
        setMissingOpen(true);
      }
      return false;
    }
    if (!readLocalEntryById(id)?.peaks) {
      addPending(id, index);
      // Asked for by hand now, whatever the restore was quietly waiting on: it takes the focus.
      restoring.delete(id);
      ensureLocalEntryAnalysed(id);
      notifications.showToast(t('studio.local.notReady'), 'info', 3000, 'local-not-ready');
      return false;
    }
    return true;
  }

  async function finishImport(result, stageIndex) {
    const { added, reattached, linked = [], alreadyHere = [], skipped } = result;
    if (skipped.length) notifications.showToast(skippedMessage(t, skipped), 'warning', 7000);
    if (added.length && localFilesAreCopied()) {
      void explainCopiesOnce(t, notifications);
      // The copies are a background job; what they could not keep is reported when they finish,
      // not guessed at now.
      void whenLocalCopiesSettled().then(() => {
        reportCopyFailures(t, notifications, added);
        publish();
      });
    }
    if (reattached.length) notifications.showToast(t('studio.local.reattached', { count: reattached.length }), 'success', 4000);
    // A file this machine already holds joins the collection without a second copy being taken.
    // Said plainly, because the card appearing with no "copying" pass looks like nothing happened.
    if (linked.length) notifications.showToast(t('studio.local.linked', { count: linked.length, title: linked[0].name }), 'success', 5000);
    if (alreadyHere.length) notifications.showToast(t('studio.local.alreadyHere', { count: alreadyHere.length }), 'info', 4000);
    publish();
    // A drop onto a stage loads what it dropped. A file whose waveform has not landed yet waits
    // for it — the same wait a single dropped file has always had, however many came with it.
    const first = added[0] ?? reattached[0] ?? linked[0] ?? alreadyHere[0] ?? null;
    if (first && Number.isInteger(stageIndex) && stageIndex >= 0) {
      if (isLocalEntryStageable(first.id)) loadStage(stageIndex, toLocalDrawerEntry(first, scopeKey));
      else addPending(first.id, stageIndex);
    }
  }

  async function dropFiles(dataTransfer, stageIndex = null) {
    if (inRoom()) {
      notifications.showToast(t('studio.local.roomDropRefused'), 'warning', 5000);
      return;
    }
    const refs = await collectDroppedFiles(dataTransfer);
    if (!refs.length) return;
    await finishImport(await importLocalFiles(refs, { scope: scopeKey, scopeName }), stageIndex);
  }

  async function pickFiles(stageIndex = null) {
    if (inRoom()) return;
    const refs = await pickLocalFiles();
    if (!refs.length) return;
    await finishImport(await importLocalFiles(refs, { scope: scopeKey, scopeName }), stageIndex);
  }

  /**
   * Put files this machine already holds onto THIS rail.
   *
   * No copy and no second entry — the entry gains a collection, which is the whole point of a file
   * belonging to a set of them. Ids already here are skipped by `addScopeToEntry` itself, so a
   * selection that overlaps what is on the rail is not an error, it is a no-op with a count.
   */
  async function addToCollection(ids) {
    if (!Array.isArray(ids) || !ids.length) return;
    const before = new Set(listLocalLibrary(scopeKey).map((entry) => entry.id));
    let added = 0;
    for (const id of ids) {
      // Counted from what the library actually DID, not from what was missing from the rail
      // beforehand. `addScopeToEntry` answers null for an entry that is no longer there — removed
      // from the local files page in this same session, say — and a count taken from the ids we
      // asked about would report files that were never added.
      const next = await addScopeToEntry(id, scopeKey);
      if (next && !before.has(id)) added += 1;
    }
    notifications.showToast(
      added
        ? t('studio.local.addedToCollection', { count: added })
        : t('studio.local.alreadyHere', { count: ids.length }),
      added ? 'success' : 'info',
      4000,
    );
    publish();
  }

  /**
   * Drop files from the local-files panel onto a stage.
   *
   * ONE stage holds one thing, so a drag carrying several loads the first and leaves the rest
   * where they were — Bruna's call, and the honest one: silently loading four onto a deck that can
   * hold one, or refusing the whole gesture, are both worse than doing the obvious thing.
   *
   * The file joins this collection on the way, because a card on a stage that is in no collection
   * would vanish from the rail the moment it is swapped out. Same staging rule a freshly dropped
   * file gets: playable now if its waveform exists, queued for the moment it lands if not.
   */
  async function stageEntries(ids, stageIndex) {
    const first = (Array.isArray(ids) ? ids : []).map((id) => readLocalEntryById(id)).find(Boolean);
    if (!first || !Number.isInteger(stageIndex) || stageIndex < 0) return;
    await addScopeToEntry(first.id, scopeKey);
    const card = toLocalDrawerEntry(readLocalEntryById(first.id) ?? first, scopeKey);
    // Through the SAME gate a card drop goes through, not around it. That gate is what refuses a
    // room, opens the locator for a file whose original has moved, asks for permission again, and
    // queues the analysis for one that has never been analysed — none of which happens on its own
    // for a file this collection had never seen. Skipping it left a stage waiting for a pass that
    // nothing had started.
    if (!beforeLoadStage(stageIndex, card)) {
      publish();
      return;
    }
    loadStage(stageIndex, card);
    publish();
  }

  async function allowAccess() {
    const granted = await requestLocalAccess(scopeKey);
    notifications.showToast(
      granted ? t('studio.local.granted', { count: granted }) : t('studio.local.grantedNone'),
      granted ? 'success' : 'info',
      4000,
    );
    publish();
  }

  async function searchFolder() {
    const result = await searchFolderForMissing(scopeKey);
    if (!result) return;
    notifications.showToast(
      result.found ? t('studio.local.missingDialog.found', { count: result.found, found: result.found, searched: result.searched }) : t('studio.local.missingDialog.noneFound'),
      result.found ? 'success' : 'info',
      5000,
    );
    publish();
  }

  function setMissingOpen(open) {
    missingOpen = Boolean(open);
    publish();
  }

  async function cardAction(id, action) {
    const entry = readLocalEntryById(id);
    if (!entry) return;
    switch (action) {
      case 'locate':
        await locateLocalEntry(id);
        break;
      case 'search-folder':
        await searchFolder();
        break;
      case 'show-path': {
        const path = entry.path || entry.folder;
        notifications.showToast(path ? t('studio.local.lastKnownPath', { path }) : t('studio.local.noKnownPath'), 'info', 8000);
        break;
      }
      case 'analyse-now':
        ensureLocalEntryAnalysed(id);
        break;
      case 'reanalyse':
        await reanalyseLocalEntry(id);
        break;
      case 'remove': {
        // Asked BEFORE the removal, because afterwards there is no entry left to ask.
        const hadCopy = Boolean(entry.copied);
        // This card is one collection's view of a file that may be in others. Leaving this rail
        // must not take the bytes another collection is still playing — so only a file whose last
        // collection this was is removed from the device.
        const { removed, copyDeleted, remainingScopes } = await removeLocalEntryFromScope(id, scopeKey);
        pendingLoads.delete(id);
        restoring.delete(id);
        if (!removed) {
          notifications.showToast(t('studio.local.leftCollection', { title: entry.name, count: remainingScopes }), 'success', 5000);
          break;
        }
        // Only say the copy went if it went. A file the browser would not release is still using
        // the disk, and claiming otherwise is the one thing this whole flow is for.
        const key = !hadCopy
          ? 'studio.local.removed'
          : copyDeleted
            ? 'studio.local.removedWithCopy'
            : 'studio.local.removedCopyStayed';
        notifications.showToast(t(key, { title: entry.name }), copyDeleted ? 'success' : 'warning', 5000);
        break;
      }
      default:
        break;
    }
    publish();
  }

  return {
    start,
    dispose,
    persistArrangement,
    notePanel,
    beforeLoadStage,
    stageChanged,
    studioProps,
    isLocal: (entry) => isLocalEntryId(entry?.voiceId),
  };
}
